import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../utils/constants';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError?: Error;
  maps?: typeof google.maps;
  apiKeyUsed: string | null;
  placesLoaded: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(undefined);

export const useGoogleMaps = (): GoogleMapsContextType => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    console.error("useGoogleMaps must be used within a GoogleMapsProvider. Defaulting to not loaded state.");
    return { 
      isLoaded: false,
      placesLoaded: false,
      loadError: new Error("useGoogleMaps context is not available. GoogleMapsProvider might not be wrapping the component tree or an issue occurred within the provider."), 
      apiKeyUsed: GOOGLE_MAPS_API_KEY || null,
      maps: undefined
    };
  }
  return context;
};

const libraries: Libraries = ["places", "routes", "marker", "geometry", "drawing"];

const GoogleMapsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [timedOut, setTimedOut] = useState(false);
  const [placesLoaded, setPlacesLoaded] = useState(false);

  useEffect(() => {
    console.log("GoogleMapsProvider: Component did mount. API Key check:");
    if (!GOOGLE_MAPS_API_KEY) {
      console.error("GoogleMapsProvider CRITICAL: GOOGLE_MAPS_API_KEY is missing or empty. Google Maps will not load.");
    } else {
      console.log("GoogleMapsProvider: GOOGLE_MAPS_API_KEY is present (length:", GOOGLE_MAPS_API_KEY.length, "). Attempting to load Google Maps API.");
    }

    // Set a timeout to detect slow loading
    const timeoutId = setTimeout(() => {
      setTimedOut(true);
      console.warn("GoogleMapsProvider: Loading timed out after 10 seconds");
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: libraries,
    preventGoogleFontsLoading: true,
    id: 'google-maps-script-loader'
  });

  useEffect(() => {
    // Validate Places library specifically
    const validatePlacesLibrary = () => {
      if (typeof window !== 'undefined' && window.google && window.google.maps) {
        const hasPlaces = !!(
          window.google.maps.places &&
          window.google.maps.places.Autocomplete &&
          window.google.maps.places.AutocompleteService
        );

        console.log("GoogleMapsProvider: Places library validation:", {
          hasGoogleObject: !!window.google,
          hasMapsObject: !!(window.google && window.google.maps),
          hasPlacesObject: !!(window.google && window.google.maps && window.google.maps.places),
          hasAutocomplete: !!(window.google && window.google.maps && window.google.maps.places && window.google.maps.places.Autocomplete),
          hasAutocompleteService: !!(window.google && window.google.maps && window.google.maps.places && window.google.maps.places.AutocompleteService)
        });

        setPlacesLoaded(hasPlaces);
        if (!hasPlaces) {
          console.error("GoogleMapsProvider: Places library failed to load properly");
        } else {
          console.log("GoogleMapsProvider: Places library successfully loaded");
        }
      }
    };

    if (isLoaded) {
      validatePlacesLibrary();
    }
  }, [isLoaded]);

  useEffect(() => {
    console.log("GoogleMapsProvider: State update:", {
      isLoaded,
      hasLoadError: !!loadError,
      errorMessage: loadError?.message,
      timedOut,
      placesLoaded
    });
  }, [isLoaded, loadError, timedOut, placesLoaded]);

  const contextValue: GoogleMapsContextType = {
    isLoaded: isLoaded && placesLoaded,
    loadError,
    maps: isLoaded && window.google && window.google.maps ? window.google.maps : undefined,
    apiKeyUsed: GOOGLE_MAPS_API_KEY || null,
    placesLoaded
  };

  console.log("GoogleMapsProvider: Providing context value:", {
    isLoaded: contextValue.isLoaded,
    error: contextValue.loadError?.message || 'null',
    apiKeyLength: contextValue.apiKeyUsed?.length || 0,
    placesLoaded: contextValue.placesLoaded
  });

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export default GoogleMapsProvider;